import { useMemo } from "react";
import { graphql } from "@acto/gql";
import { useQuery } from "urql";
import { DataAccessResult } from "@acto/taskpane/core/data-access/types";
import { Todo } from "@acto/taskpane/core/domain";

const TodosByIdsQuery = graphql(`
  query GetTodosByIds($ids: [ID!]!) {
    todosByIds(ids: $ids) {
      id
      __typename
      title
      description
      dueDate
      processedStatus
      processedReason
      isWorkflow
      isNoteBased
      client {
        id
        legacyId
        __typename
        name
        externalReference
      }
      assignedUser {
        id
        name
        __typename
        employee {
          id
          name
          __typename
        }
      }
    }
  }
`);

/**
 * Todos for an explicit id list. Status is read live, so a todo completed elsewhere arrives as COMPLETED and unknown ids are
 * simply absent. Paused for an empty list; `cache-and-network` so a remount shows cached rows
 * immediately and still refreshes.
 */
export function useTodosByIds(ids: readonly string[]): DataAccessResult<Todo> {
  const variables = useMemo(() => ({ ids: [...ids] }), [ids]);
  const [result] = useQuery({
    query: TodosByIdsQuery,
    variables,
    pause: ids.length === 0,
    requestPolicy: "cache-and-network",
  });

  // urql can briefly report the previous operation's result right after `variables` change. Ignore it
  // so we never expose another id list's todos.
  const operationIds = (result.operation?.variables as { ids?: readonly string[] } | undefined)?.ids;
  const matchesIds = !result.operation || (operationIds ?? []).join("|") === ids.join("|");
  const data = matchesIds ? result.data : undefined;
  const error = matchesIds ? result.error : undefined;
  const isFetching = result.fetching;
  const isPending = ids.length > 0 && !data && isFetching;
  const todos = useMemo(() => data?.todosByIds.map(Todo.fromGraphql), [data]);

  return { data: todos, isFetching, isPending, error };
}
